import React, { useState, useCallback, useEffect, useRef } from "react";
import moment from "moment";
import {
  Banner,
  Modal,
  BlockStack,
  TextField,
  Select,
  DropZone,
  Text,
  InlineStack,
  InlineGrid,
  Button,
  Checkbox,
  Divider,
} from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { clearOpenSectionBillableAmount } from "../../utility/ReservationUtilities";

// Latest inspection of this type for the reservation, finalized or not. A
// reservation has at most one per type (reverting deletes the row), so any
// row found here is the one to append to.
async function fetchExistingInspection(reservationId, type) {
  const { data } = await supabase
    .from("reservation_inspections")
    .select("id, odometer, fuel_level, notes, signed_by_name, signed_at, signature_image_url, signature_confirmed, revert_snapshot, reservation_inspection_photos(id)")
    .eq("reservation_id", reservationId)
    .eq("type", type)
    .order("recorded_at", { ascending: false })
    .limit(1);
  return data?.[0] || null;
}

function InspectionModal({
  open,
  onClose,
  reservationId,
  type,
  onComplete,
  currentOdometer,
  scheduledDate,
}) {
  const { session } = UserAuth();

  // Parse scheduled date for this inspection type
  const getScheduledDateTime = () => {
    if (!scheduledDate) return { date: moment().format("YYYY-MM-DD"), time: moment().format("HH:mm") };
    try {
      const clean = scheduledDate.replace(/[\[\]()]/g, "");
      const parts = clean.split(",");
      const str = type === "checkout"
        ? parts[0]?.replace(/"/g, "").trim()
        : parts[1]?.replace(/"/g, "").trim();
      if (str) {
        // Read raw date/time as stored — app treats DB times as local throughout
        return {
          date: str.substring(0, 10),
          time: str.substring(11, 16) || "09:00",
        };
      }
    } catch {}
    return { date: moment().format("YYYY-MM-DD"), time: moment().format("HH:mm") };
  };

  const [odometer, setOdometer] = useState(currentOdometer?.toString() || "");
  const [fuelLevel, setFuelLevel] = useState("16");
  const [notes, setNotes] = useState("");
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inspectionDate, setInspectionDate] = useState("");
  const [inspectionTime, setInspectionTime] = useState("");
  // When the iPad app already captured an inspection for this reservation+type
  // (with photos, signed or not), we reuse that row instead of inserting a new
  // one — otherwise the new, photo-less row becomes "latest" and the app's
  // photos look deleted. Signed rows are read-only; unsigned ones stay editable.
  const [existingInspection, setExistingInspection] = useState(null);
  const existingSigned = !!existingInspection?.signature_image_url;
  const [deliveryFuelLevel, setDeliveryFuelLevel] = useState(null);
  const [tankCapacity, setTankCapacity] = useState(null);
  const [fuelCost, setFuelCost] = useState(null);
  const [addFuelCharge, setAddFuelCharge] = useState(true);
  const [fuelChargeAmount, setFuelChargeAmount] = useState("");
  const [fuelChargeEdited, setFuelChargeEdited] = useState(false);

  // Reset form + look up any existing signed inspection when modal opens
  useEffect(() => {
    if (!open) return;
    const s = getScheduledDateTime();
    setInspectionDate(s.date);
    setInspectionTime(s.time);
    setSignatureConfirmed(false);
    setPhotos([]);
    setExistingInspection(null);
    setOdometer(currentOdometer?.toString() || "");
    setFuelLevel("16");
    setNotes("");
    setDeliveryFuelLevel(null);
    setTankCapacity(null);
    setFuelCost(null);
    setAddFuelCharge(true);
    setFuelChargeAmount("");
    setFuelChargeEdited(false);

    (async () => {
      const latest = await fetchExistingInspection(reservationId, type);
      if (latest) {
        setExistingInspection(latest);
        if (latest.odometer) setOdometer(latest.odometer.toString());
        setFuelLevel(latest.fuel_level || "16");
        setNotes(latest.notes || "");
        setSignatureConfirmed(!!latest.signature_image_url || !!latest.signature_confirmed);
        // Default to when the iPad captured it, not the scheduled date — for
        // long rentals the scheduled end can be months away.
        if (latest.signed_at) {
          const signed = moment.utc(latest.signed_at);
          setInspectionDate(signed.format("YYYY-MM-DD"));
          setInspectionTime(signed.format("HH:mm"));
        }
      }

      // For return inspections, look up delivery fuel level + tank capacity + fuel cost
      if (type === "checkin") {
        const { data: deliv } = await supabase
          .from("reservation_inspections")
          .select("fuel_level")
          .eq("reservation_id", reservationId)
          .eq("type", "checkout")
          .order("created_at", { ascending: false })
          .limit(1);
        if (deliv?.[0]?.fuel_level) setDeliveryFuelLevel(deliv[0].fuel_level);

        const { data: res } = await supabase
          .from("reservations")
          .select("car:cars(model:car_models(tank_capacity, fuel_type:fuel_type(cost)))")
          .eq("id", reservationId)
          .single();
        const model = res?.car?.model;
        if (model?.tank_capacity) setTankCapacity(Number(model.tank_capacity));
        if (model?.fuel_type?.cost) setFuelCost(Number(model.fuel_type.cost));
      }
    })();
  }, [open, scheduledDate, type, reservationId, currentOdometer]);

  // Compute suggested fuel charge from the diff (eighths/16 × tank × cost)
  const fuelDeficitEighths = Math.max(
    0,
    parseInt(deliveryFuelLevel || "0") - parseInt(fuelLevel || "0")
  );
  const suggestedFuelCharge =
    tankCapacity && fuelCost && fuelDeficitEighths > 0
      ? Math.round((fuelDeficitEighths / 16) * tankCapacity * fuelCost)
      : 0;

  useEffect(() => {
    // Keep the amount in sync with the suggestion unless the admin has edited it
    if (!fuelChargeEdited) {
      setFuelChargeAmount(suggestedFuelCharge > 0 ? String(suggestedFuelCharge) : "");
    }
  }, [suggestedFuelCharge, fuelChargeEdited]);

  const fuelOptions = [
    { label: "16/16 (Lleno)", value: "16" },
    { label: "14/16", value: "14" },
    { label: "12/16 (3/4)", value: "12" },
    { label: "10/16", value: "10" },
    { label: "8/16 (1/2)", value: "8" },
    { label: "6/16", value: "6" },
    { label: "4/16 (1/4)", value: "4" },
    { label: "2/16", value: "2" },
    { label: "0/16 (Vacío)", value: "0" },
  ];

  const handlePhotoDrop = useCallback((_drop, accepted) => {
    setPhotos((prev) => [...prev, ...accepted]);
  }, []);

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const savingRef = useRef(false);
  const save = async () => {
    // Block reentry from rapid double-clicks before React flushes setSaving(true).
    if (savingRef.current) return;
    // Times are face value (local clock), so compare against local now.
    if (`${inspectionDate} ${inspectionTime}` > moment().format("YYYY-MM-DD HH:mm")) {
      alert("La fecha/hora no puede ser futura. Captura la hora real de la " +
        (type === "checkout" ? "entrega." : "devolución."));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const userId = session?.user?.id;

    // Store as-is without timezone conversion — app treats all times as face value
    const inspectionTimestamp = `${inspectionDate} ${inspectionTime}:00+00`;

    // Snapshot the exact reservation/car state this inspection is about to
    // overwrite, so the whole entrega/devolución can be reverted later. Read
    // it BEFORE any mutation. Persisted on the inspection row as revert_snapshot.
    const { data: preRes } = await supabase
      .from("reservations")
      .select("date_range, km_start, km_end, checked_out, checked_in, car")
      .eq("id", reservationId)
      .single();
    const revertSnapshot = {
      date_range: preRes?.date_range ?? null,
      km_start: preRes?.km_start ?? null,
      km_end: preRes?.km_end ?? null,
      checked_out: preRes?.checked_out ?? false,
      checked_in: preRes?.checked_in ?? false,
    };

    // Look again: the iPad may have submitted while this modal was open.
    const existing = await fetchExistingInspection(reservationId, type);
    // A row that appeared after opening wasn't shown in the form, so the
    // form's values must not overwrite it — only append to it.
    const seenAtOpen = existing && existing.id === existingInspection?.id;

    // Track the inspection row this save operates on, so a fuel charge (below)
    // can be linked to it for precise revert deletion.
    let inspectionRowId = existing?.id ?? null;

    // If the iPad app already created an inspection, reuse it instead of
    // creating a new row (a new row would hide the app's photos).
    if (existing) {
      // A row without a snapshot gets one now so this entrega/devolución is
      // revertible, and recorded_at resets to this confirmation moment so the
      // 2h revert window starts when the admin actually finalizes. A row that
      // already has one keeps it (it holds the state before the first
      // finalization). Unsigned rows shown in the form take the admin's
      // (editable) values; otherwise typed notes are appended.
      // flags + date_range update happens below (shared code path).
      const typedNotes = notes.trim();
      const { error } = await supabase
        .from("reservation_inspections")
        .update({
          ...(existing.revert_snapshot
            ? {}
            : { revert_snapshot: revertSnapshot, recorded_at: new Date().toISOString() }),
          ...(seenAtOpen && !existing.signature_image_url
            ? {
                odometer: parseInt(odometer) || 0,
                fuel_level: fuelLevel,
                notes: typedNotes || null,
                signature_confirmed: signatureConfirmed,
                created_at: inspectionTimestamp,
              }
            : typedNotes && typedNotes !== (existing.notes || "").trim()
            ? { notes: [existing.notes, typedNotes].filter(Boolean).join("\n") }
            : {}),
        })
        .eq("id", existing.id);
      if (error) {
        alert("Error: " + error.message);
        setSaving(false);
        savingRef.current = false;
        return;
      }
    } else {
      // Create inspection record
      const { data: inspection, error } = await supabase
        .from("reservation_inspections")
        .insert({
          reservation_id: reservationId,
          type,
          odometer: parseInt(odometer) || 0,
          fuel_level: fuelLevel,
          notes: notes.trim() || null,
          created_by: userId,
          signature_confirmed: signatureConfirmed,
          created_at: inspectionTimestamp,
          revert_snapshot: revertSnapshot,
        })
        .select("id")
        .single();

      if (error) {
        alert("Error: " + error.message);
        setSaving(false);
        savingRef.current = false;
        return;
      }
      inspectionRowId = inspection.id;
    }

    // Upload any photos added here to the inspection row (new or reused)
    for (const photo of photos) {
      const safeName = photo.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${reservationId}/${type}_${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("inspection-photos")
        .upload(filePath, photo, { upsert: true });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        continue;
      }

      await supabase.from("reservation_inspection_photos").insert({
        inspection_id: inspectionRowId,
        file_url: filePath,
        file_name: photo.name,
        label: type === "checkout" ? "Entrega" : "Devolución",
      });
    }

    // Update reservation flags and adjust date_range to actual times
    const updates = {};
    if (type === "checkout") {
      updates.checked_out = true;
      updates.km_start = parseInt(odometer) || null;
    } else {
      updates.checked_in = true;
      updates.km_end = parseInt(odometer) || null;
    }

    // Get current date_range to update the actual start or end
    const { data: currentRes } = await supabase
      .from("reservations")
      .select("date_range")
      .eq("id", reservationId)
      .single();

    if (currentRes?.date_range) {
      const clean = currentRes.date_range.replace(/[\[\]()]/g, "");
      const parts = clean.split(",");
      const currentStart = parts[0]?.replace(/"/g, "").trim();
      const currentEnd = parts[1]?.replace(/"/g, "").trim();

      if (type === "checkout") {
        // Update start time to actual pickup time
        updates.date_range = `[${inspectionTimestamp},${currentEnd || ""}]`;
      } else {
        // Update end time to actual return time
        updates.date_range = `[${currentStart},${inspectionTimestamp}]`;
      }

      // Clear the open section's billable_amount before the date_range
      // UPDATE so compute_reservation_total_rent recomputes from
      // days × daily_price for the corrected pickup/return window instead
      // of returning the pre-checkout frozen total.
      await clearOpenSectionBillableAmount(reservationId);
    }

    await supabase
      .from("reservations")
      .update(updates)
      .eq("id", reservationId);

    // Auto-add fuel charge if return inspection and amount > 0
    if (type === "checkin" && addFuelCharge) {
      const amount = parseFloat(fuelChargeAmount);
      if (amount > 0) {
        const liters =
          tankCapacity && fuelDeficitEighths > 0
            ? Math.round(((fuelDeficitEighths / 16) * tankCapacity) * 100) / 100
            : null;
        const desc =
          liters != null
            ? `Gasolina faltante (~${liters} L: ${deliveryFuelLevel}/16 → ${fuelLevel}/16)`
            : "Gasolina faltante";
        // DB trigger trg_reservation_charges_sync_total handles total_price / balance.
        await supabase.from("reservation_charges").insert({
          reservation_id: reservationId,
          charge_type: "fuel",
          description: desc,
          amount,
          created_by: userId,
          inspection_id: inspectionRowId,
        });
      }
    }

    // The car's odometer is not set from the typed km: the dashboard photo
    // opens an odometer request (inspection_odometer), and approving it
    // calibrates the car in Nexofleet.

    setSaving(false);
    savingRef.current = false;
    onComplete();
    onClose();

    // Form resets via useEffect when modal reopens
  };

  const title =
    type === "checkout" ? "Inspección de Entrega" : "Inspección de Devolución";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      primaryAction={{
        content: saving
          ? "Guardando..."
          : existingSigned
          ? (type === "checkout" ? "Confirmar Entrega" : "Confirmar Devolución")
          : "Guardar Inspección",
        onAction: save,
        loading: saving,
        disabled: !odometer || saving,
      }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
      large
    >
      <Modal.Section>
        <BlockStack gap="400">
          {existingInspection && !existingSigned && (
            <Banner tone="info" title="Inspección capturada desde la app">
              <p>
                Ya existe una inspección con{" "}
                {(existingInspection.reservation_inspection_photos || []).length} foto(s).
                Se conservará y se completará con estos datos.
              </p>
            </Banner>
          )}
          {existingSigned && (
            <Banner tone="success" title="Inspección firmada desde iPad">
              <p>
                Los datos fueron capturados y firmados por{" "}
                <strong>{existingInspection.signed_by_name || "el cliente"}</strong>
                {existingInspection.signed_at
                  ? ` el ${moment.utc(existingInspection.signed_at).format("DD/MM/YYYY HH:mm")}`
                  : ""}.
                Confirma la hora real en que se entregan las llaves para finalizar.
              </p>
            </Banner>
          )}
          <InlineGrid columns={2} gap="200">
            <TextField
              label="Fecha"
              type="date"
              value={inspectionDate}
              onChange={setInspectionDate}
            />
            <TextField
              label="Hora"
              type="time"
              value={inspectionTime}
              onChange={setInspectionTime}
            />
          </InlineGrid>
          <TextField
            label="Odómetro"
            value={odometer}
            onChange={setOdometer}
            type="number"
            suffix="km"
            requiredIndicator
            disabled={existingSigned}
            helpText={existingSigned ? "Prellenado de la inspección firmada." : undefined}
          />
          <Select
            label="Nivel de Combustible"
            value={fuelLevel}
            options={fuelOptions}
            onChange={setFuelLevel}
            disabled={existingSigned}
            helpText={existingSigned ? "Prellenado de la inspección firmada." : undefined}
          />
          {type === "checkin" && deliveryFuelLevel && suggestedFuelCharge > 0 && (
            <Banner tone="warning" title="Cargo por gasolina faltante">
              <BlockStack gap="200">
                <Text>
                  Entrega: {deliveryFuelLevel}/16 → Devolución: {fuelLevel}/16
                  {tankCapacity
                    ? ` · ${Math.round(
                        ((fuelDeficitEighths / 16) * tankCapacity) * 100
                      ) / 100} L`
                    : ""}
                  {fuelCost ? ` × $${fuelCost}/L` : ""}
                </Text>
                <Checkbox
                  label="Agregar cargo automáticamente"
                  checked={addFuelCharge}
                  onChange={setAddFuelCharge}
                />
                {addFuelCharge && (
                  <TextField
                    label="Monto"
                    type="number"
                    prefix="$"
                    value={fuelChargeAmount}
                    onChange={(val) => {
                      setFuelChargeAmount(val);
                      setFuelChargeEdited(true);
                    }}
                    helpText={
                      fuelChargeEdited
                        ? `Sugerido: $${suggestedFuelCharge}`
                        : undefined
                    }
                  />
                )}
              </BlockStack>
            </Banner>
          )}
          <TextField
            label="Notas"
            value={notes}
            onChange={setNotes}
            multiline={3}
            placeholder="Estado general del vehículo, daños existentes, etc."
            disabled={existingSigned}
          />

          {!existingSigned && (
            <>
              <Divider />
              <Text variant="headingSm">Fotos del Vehículo</Text>
              <DropZone onDrop={handlePhotoDrop} accept="image/*" variableHeight>
                <DropZone.FileUpload actionTitle="Subir fotos" />
              </DropZone>
              {photos.length > 0 && (
                <BlockStack gap="100">
                  {photos.map((f, i) => (
                    <InlineStack key={i} align="space-between" blockAlign="center">
                      <Text variant="bodySm">{f.name}</Text>
                      <Button
                        icon={DeleteIcon}
                        variant="plain"
                        tone="critical"
                        onClick={() => removePhoto(i)}
                      />
                    </InlineStack>
                  ))}
                </BlockStack>
              )}
            </>
          )}

          {!existingSigned && (
            <>
              <Divider />
              <Checkbox
                label="Cliente confirmó el estado del vehículo"
                checked={signatureConfirmed}
                onChange={setSignatureConfirmed}
              />
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default InspectionModal;
